/**
 * Expense Ledger Read Bridge (Phase C)
 * ------------------------------------
 * READ-ONLY consolidation of realized job costs from the canonical
 * `expense_ledger` model (db/migrations/0003_expense_ledger.sql).
 *
 * The DB ledger is append-only and category-locked. This bridge mirrors
 * its enum shape in TS, accepts an array of entries scoped to a job, and
 * resolves per-category realized totals that the job cost breakdown can
 * use to PROMOTE previously-missing lines (permits, financing, callback,
 * warranty, field-add, commission, etc.) from `missing` → `realized`.
 *
 * This bridge does NOT:
 *   - write to the ledger (Edge Function `expense-ledger-write` only)
 *   - reconcile vendor invoices against PO totals (Phase D)
 *   - allocate overhead, freight, restocking, or returns (Phase E)
 *   - infer sales commission from any other source — only entries with
 *     source === "commission_engine" are accepted for that category
 *     (mirrors the DB trigger guard)
 *
 * If no entries are provided for a category, the bridge returns nothing
 * for it and the job cost breakdown keeps its existing `missing` line —
 * Phase A invariant: never silently zero an untracked category.
 */

// ── Mirrored DB enums (db/migrations/0003_expense_ledger.sql) ──

export type ExpenseLedgerCategory =
  | "materials"
  | "labor"
  | "subcontractor_payout"
  | "callback_labor"
  | "warranty_remediation"
  | "permits"
  | "dumpster_disposal"
  | "fuel_logistics"
  | "field_add"
  | "vendor_invoice"
  | "financing_fees"
  | "lead_acquisition_cac"
  | "appointment_generation_cost"
  | "sales_commission"
  | "misc_operational";

export type ExpenseLedgerSource =
  | "manual_entry"
  | "commission_engine"
  | "vendor_invoice_import"
  | "payroll_import"
  | "system";

export interface ExpenseLedgerEntry {
  id: string;
  jobId?: string | null;
  category: ExpenseLedgerCategory;
  source: ExpenseLedgerSource;
  /** Positive cents. Ledger enforces `<> 0`. */
  amountCents: number;
  occurredOn: string; // ISO date
  memo?: string | null;
  vendorId?: string | null;
  referenceId?: string | null;
  createdAt?: string;
}

export interface ExpenseCategoryRollup {
  /** Resolved dollar amount, summed from entries. */
  amount: number;
  entryCount: number;
  source: ExpenseLedgerSource | "mixed";
  /** Most recent occurrence date in the bucket, ISO. */
  lastOccurredOn: string;
}

export interface JobExpenseLedgerRollup {
  jobId: string;
  totalEntries: number;
  /**
   * Per-category realized rollups. A category is only present when ≥1
   * valid entry exists for it. Absence ≠ zero — see Phase A invariant.
   */
  byCategory: Partial<Record<ExpenseLedgerCategory, ExpenseCategoryRollup>>;
  /** Entries dropped because they violated the commission_engine guard. */
  rejectedCount: number;
}

/**
 * Bucket job-scoped expense entries by category. Mirrors the DB trigger:
 * sales_commission rows MUST originate from `commission_engine`; any
 * other source for that category is dropped (NOT counted, NOT zeroed —
 * just excluded from the rollup).
 */
export function getJobExpenseLedgerRollup(
  jobId: string,
  entries: ExpenseLedgerEntry[] = [],
): JobExpenseLedgerRollup {
  const scoped = entries.filter((e) => e.jobId === jobId);
  const byCategory: Partial<Record<ExpenseLedgerCategory, ExpenseCategoryRollup>> = {};
  let rejected = 0;
  let accepted = 0;

  for (const entry of scoped) {
    if (
      entry.category === "sales_commission" &&
      entry.source !== "commission_engine"
    ) {
      rejected += 1;
      continue;
    }
    if (!Number.isFinite(entry.amountCents) || entry.amountCents === 0) {
      rejected += 1;
      continue;
    }
    accepted += 1;
    const bucket = byCategory[entry.category];
    const dollars = entry.amountCents / 100;
    if (!bucket) {
      byCategory[entry.category] = {
        amount: dollars,
        entryCount: 1,
        source: entry.source,
        lastOccurredOn: entry.occurredOn,
      };
    } else {
      bucket.amount += dollars;
      bucket.entryCount += 1;
      bucket.source = bucket.source === entry.source ? bucket.source : "mixed";
      if (entry.occurredOn > bucket.lastOccurredOn) {
        bucket.lastOccurredOn = entry.occurredOn;
      }
    }
  }

  return {
    jobId,
    totalEntries: accepted,
    byCategory,
    rejectedCount: rejected,
  };
}

/**
 * Convenience: did the ledger record a realized amount for this category?
 * Used by the job cost breakdown to decide whether to promote a missing
 * line to realized — never the other way around.
 */
export function hasRealizedExpense(
  rollup: JobExpenseLedgerRollup,
  category: ExpenseLedgerCategory,
): boolean {
  const bucket = rollup.byCategory[category];
  return !!bucket && bucket.amount > 0;
}
